import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, User, MessageSquare, Sparkles, CheckCircle, Phone } from 'lucide-react';
import { BlurFade } from './ui/blur-fade';
import { buildWhatsAppUrl, WHATSAPP_NUMBER_DISPLAY } from '../config/contact';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  // Validasyon fonksiyonları
  const validateName = (name) => {
    if (!name.trim()) return 'Ad alanı zorunludur';
    if (name.trim().length < 2) return 'Ad en az 2 karakter olmalıdır';
    return '';
  };

  const validateEmail = (email) => {
    if (!email.trim()) return 'E-posta alanı zorunludur';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Geçerli bir e-posta adresi giriniz';
    return '';
  };

  const validatePhone = (phone) => {
    if (!phone.trim()) return 'Telefon numarası zorunludur';
    // Türk telefon formatı: 05XX XXX XX XX veya 5XXXXXXXXX
    const phoneRegex = /^(05|5)([0-9]{9})$/;
    const cleanPhone = phone.replace(/\s/g, '');
    if (!phoneRegex.test(cleanPhone)) return 'Geçerli bir telefon numarası giriniz (05XX XXX XX XX)';
    return '';
  };

  const validateMessage = (message) => {
    if (!message.trim()) return 'Mesaj alanı zorunludur';
    if (message.trim().length < 10) return 'Mesaj en az 10 karakter olmalıdır';
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Real-time validasyon
    let error = '';
    switch (name) {
      case 'name':
        error = validateName(value);
        break;
      case 'email':
        error = validateEmail(value);
        break;
      case 'phone':
        error = validatePhone(value);
        break;
      case 'message':
        error = validateMessage(value);
        break;
      default:
        break;
    }
    
    setErrors({ ...errors, [name]: error });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Tüm alanları doğrula
    const newErrors = {
      name: validateName(formData.name),
      email: validateEmail(formData.email),
      phone: validatePhone(formData.phone),
      message: validateMessage(formData.message)
    };

    setErrors(newErrors);

    // Hata varsa submit etme
    const hasErrors = Object.values(newErrors).some(error => error !== '');
    if (hasErrors) return;

    setIsSubmitting(true);
    
    // WhatsApp URL'sini önceden hazırla (tek doğruluk kaynağı: /config/contact.js)
    const whatsappText = `Yeni İletişim Formu\n\nAd: ${formData.name}\nE-posta: ${formData.email}\nTelefon: ${formData.phone}\n\nMesaj: ${formData.message}`;
    const whatsappUrl = buildWhatsAppUrl(whatsappText);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error('Mesaj gönderilemedi');
      }
    } catch (error) {
      console.error('Contact form backend error:', error);
      // Backend hatası olsa bile WhatsApp'a yönlendir
    }
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    setFormData({ name: '', email: '', phone: '', message: '' });
    setErrors({});
    
    // WhatsApp'a direkt yönlendir (popup blocker engellemez)
    window.location.href = whatsappUrl;
  };

  return (
    <section id="iletisim" className="relative py-24 md:py-32 bg-black overflow-hidden scroll-mt-20">
      {/* Background Effects */}
      <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-purple-600/10 rounded-full filter blur-[150px]"></div>
      <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] bg-pink-600/10 rounded-full filter blur-[120px]"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/5 rounded-full filter blur-[180px]"></div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {/* Left Side - Text Content */}
          <div className="text-center lg:text-left">
            <BlurFade delay={0} inView>
              <div className="flex items-center justify-center lg:justify-start gap-3 mb-6">
                <div className="h-px w-12 bg-gradient-to-r from-transparent to-purple-500"></div>
                <Sparkles className="w-5 h-5 text-purple-400" />
                <span className="text-purple-400 text-sm tracking-widest uppercase">İletişim</span>
              </div>
            </BlurFade>

            <BlurFade delay={0.1} inView>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-light text-white mb-6 tracking-tight">
                Dönüşüm Yolculuğunuz{' '}
                <span className="relative inline-block">
                  <span className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 blur-2xl opacity-30"></span>
                  <span className="relative bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                    Burada Başlıyor
                  </span>
                </span>
              </h2>
            </BlurFade>

            <BlurFade delay={0.2} inView>
              <p className="text-gray-400 text-lg mb-8 leading-relaxed max-w-lg mx-auto lg:mx-0">
                Sorularınız, merak ettikleriniz veya birlikte çalışma talepleriniz için 
                benimle iletişime geçin. Her mesajı özenle değerlendiriyorum.
              </p>
            </BlurFade>

            <BlurFade delay={0.3} inView>
              <div className="space-y-4">
                <div className="flex items-center gap-4 justify-center lg:justify-start">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-purple-400" />
                  </div>
                  <span className="text-gray-300">info@tanertorun.com</span>
                </div>
              </div>
            </BlurFade>
          </div>

          {/* Right Side - Contact Form */}
          <BlurFade delay={0.2} inView>
            <div className="relative">
              {/* Subtle Form Card Glow */}
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-600/5 via-pink-600/5 to-purple-600/5 rounded-3xl blur-2xl"></div>
              
              {/* Form Card */}
              <motion.div 
                className="relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                {isSubmitted ? (
                  <motion.div 
                    className="text-center py-12"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white/5 border border-white/20 flex items-center justify-center">
                      <CheckCircle className="w-10 h-10 text-gray-300" />
                    </div>
                    <h3 className="text-2xl font-light text-white mb-2">Mesajınız İletildi</h3>
                    <p className="text-gray-400 font-light">En kısa sürede size dönüş yapacağım.</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name Field */}
                    <div className="relative">
                      <label className="block text-sm text-gray-400 mb-2 ml-1 font-light">Adınız</label>
                      <div className="relative">
                        <div className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-300 ${
                          focusedField === 'name' ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          <User className="w-5 h-5" />
                        </div>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('name')}
                          onBlur={() => setFocusedField(null)}
                          className={`w-full bg-white/[0.03] border ${
                            errors.name ? 'border-red-500/50' : 'border-white/10'
                          } rounded-xl py-4 pl-12 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-white/20 focus:bg-white/[0.05] transition-all duration-300 font-light`}
                          placeholder="Adınızı girin"
                        />
                      </div>
                      {errors.name && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-400 text-xs mt-1 ml-1"
                        >
                          {errors.name}
                        </motion.p>
                      )}
                    </div>

                    {/* Email Field */}
                    <div className="relative">
                      <label className="block text-sm text-gray-400 mb-2 ml-1 font-light">E-posta</label>
                      <div className="relative">
                        <div className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-300 ${
                          focusedField === 'email' ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          <Mail className="w-5 h-5" />
                        </div>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('email')}
                          onBlur={() => setFocusedField(null)}
                          className={`w-full bg-white/[0.03] border ${
                            errors.email ? 'border-red-500/50' : 'border-white/10'
                          } rounded-xl py-4 pl-12 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-white/20 focus:bg-white/[0.05] transition-all duration-300 font-light`}
                          placeholder="E-posta adresinizi girin"
                        />
                      </div>
                      {errors.email && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-400 text-xs mt-1 ml-1"
                        >
                          {errors.email}
                        </motion.p>
                      )}
                    </div>

                    {/* Phone Field */}
                    <div className="relative">
                      <label className="block text-sm text-gray-400 mb-2 ml-1 font-light">Telefon</label>
                      <div className="relative">
                        <div className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-300 ${
                          focusedField === 'phone' ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          <Phone className="w-5 h-5" />
                        </div>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('phone')}
                          onBlur={() => setFocusedField(null)}
                          className={`w-full bg-white/[0.03] border ${
                            errors.phone ? 'border-red-500/50' : 'border-white/10'
                          } rounded-xl py-4 pl-12 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-white/20 focus:bg-white/[0.05] transition-all duration-300 font-light`}
                          placeholder="05XX XXX XX XX"
                        />
                      </div>
                      {errors.phone && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-400 text-xs mt-1 ml-1"
                        >
                          {errors.phone}
                        </motion.p>
                      )}
                    </div>

                    {/* Message Field */}
                    <div className="relative">
                      <label className="block text-sm text-gray-400 mb-2 ml-1 font-light">Mesajınız</label>
                      <div className="relative">
                        <div className={`absolute left-4 top-4 transition-colors duration-300 ${
                          focusedField === 'message' ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          <MessageSquare className="w-5 h-5" />
                        </div>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          onFocus={() => setFocusedField('message')}
                          onBlur={() => setFocusedField(null)}
                          rows={4}
                          className={`w-full bg-white/[0.03] border ${
                            errors.message ? 'border-red-500/50' : 'border-white/10'
                          } rounded-xl py-4 pl-12 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-white/20 focus:bg-white/[0.05] transition-all duration-300 resize-none font-light`}
                          placeholder="Mesajınızı yazın..."
                        />
                      </div>
                      {errors.message && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-400 text-xs mt-1 ml-1"
                        >
                          {errors.message}
                        </motion.p>
                      )}
                    </div>

                    {/* Submit Button - Elegant Glassmorphism Style */}
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className="relative w-full group overflow-hidden"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {/* Elegant Glass Background */}
                      <div className="absolute inset-0 bg-white/5 backdrop-blur-md border border-white/10 rounded-xl"></div>
                      
                      {/* Subtle Shimmer Effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/5 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl"></div>
                      
                      {/* Button Content */}
                      <div className="relative flex items-center justify-center gap-3 py-4 px-6">
                        {isSubmitting ? (
                          <motion.div
                            className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          />
                        ) : (
                          <>
                            <span className="text-white font-light">Mesaj Gönder</span>
                            <Send className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform duration-300" />
                          </>
                        )}
                      </div>
                    </motion.button>
                  </form>
                )}
              </motion.div>
            </div>
          </BlurFade>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
